"use client";

import { useState } from "react";
import { format } from "date-fns";
import { formatShortDate } from "@/lib/date/format-short-date";
import { type DateRange } from "react-day-picker";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { filterSegmentTriggerClassName } from "./shared";
import { Icon } from "@/components/ui/icon";

export function parseDateRange(value: string): DateRange | undefined {
  if (!value) return undefined;
  const [fromStr, toStr] = value.split("|");
  const from = fromStr ? new Date(fromStr) : undefined;
  const to = toStr ? new Date(toStr) : undefined;
  if (from && isNaN(from.getTime())) return undefined;
  if (to && isNaN(to.getTime())) return undefined;
  return from ? { from, to: to ?? undefined } : undefined;
}

export function serializeDateRange(range: DateRange | undefined): string {
  if (!range?.from) return "";
  // The calendar yields picked days at local midnight. Consumers compare
  // `occurred_at <= to`, so serializing the end bound as-picked would
  // exclude the entire end day the user chose; pin it to the end of that
  // local day instead. Idempotent on re-parse, so reopening the panel
  // does not drift the bound.
  let to = "";
  if (range.to) {
    const end = new Date(range.to);
    end.setHours(23, 59, 59, 999);
    to = end.toISOString();
  }
  return `${range.from.toISOString()}|${to}`;
}

function formatDateRangeLabel(value: string): string {
  const range = parseDateRange(value);
  if (!range?.from) return "Pick dates…";
  if (!range.to) return formatShortDate(range.from);
  return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}`;
}

export function DateRangeSegment({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const range = parseDateRange(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(filterSegmentTriggerClassName, "whitespace-nowrap text-contrast/75")}
        >
          <span>{formatDateRangeLabel(value)}</span>
          <Icon name="IconChevronGrabberVertical" size={10} className="text-heading opacity-20 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-auto p-0">
        <Calendar
          mode="range"
          selected={range}
          onSelect={(r) => {
            onChange(serializeDateRange(r));
            if (r?.from && r?.to) setOpen(false);
          }}
          numberOfMonths={2}
          defaultMonth={range?.from}
        />
      </PopoverContent>
    </Popover>
  );
}
